import { PinType } from '@store/weathermap.store';
import { AddLayerObject, FilterSpecification } from 'maplibre-gl';

export const PIN_TYPE_CACHE_KEY = 'weathermap-pin-type';
export const RAIN_SCALE_CACHE_KEY = 'weathermap-rain-scale';
export const TEMPERATURE_QUALITY_CACHE_KEY = 'weathermap-temperature-quality';
export const WIND_TYPE_CACHE_KEY = 'weathermap-wind-type';

export const STATION_ID_CACHE_KEY = 'weathermap-station-id';

export const MAP_CENTER_CACHE_KEY = 'weathermap-map-center';
export const MAP_ZOOM_CACHE_KEY = 'weathermap-map-zoom';

export const UNIT_SYSTEM_CACHE_KEY = 'weathermap-unit-system';
export const UNIT_WIND_CACHE_KEY = 'weathermap-unit-wind';
export const UNIT_PRESSURE_CACHE_KEY = 'weathermap-unit-pressure';

export const JawgToken = 'JZZDGPDXdB5O7KQTcV2h2GTi4248rdzuHpspoAGPSryAs1yvlAaZ118vaSrxzQ69';

export const ZoomFilter: FilterSpecification = [
  'any',
  ['!', ['has', 'zoomLevel']],
  ['==', ['get', 'zoomLevel'], ['zoom']],
];

function TemperatureLayer(id: string, options?: { sourceLayer: boolean }): AddLayerObject {
  const layer = {
    id,
    source: id,
    type: 'symbol',
    filter: ZoomFilter,
    layout: {
      'icon-image': [
        'concat',
        'temperature-',
        ['get', 'temperatureUnit'],
        '-',
        [
          'match',
          ['get', 'temperatureUnit'],
          'metric',
          ['get', 'temperature'],
          ['to-string', ['round', ['+', ['*', ['/', 9, 5], ['get', 'temperature']], 32]]],
        ],
      ],
      'icon-size': 0.5,
      'icon-offset': [0, -39],
      'icon-allow-overlap': true,
    },
    paint: {
      'text-color': 'white',
    },
  } as AddLayerObject;

  if (options?.sourceLayer) {
    layer['source-layer'] = id;
  }

  return layer;
}

function RainLayer(id: string, options?: { sourceLayer: boolean }): AddLayerObject {
  const layer = {
    id,
    source: id,
    type: 'symbol',
    filter: ZoomFilter,
    layout: {
      'icon-image': [
        'case',
        ['==', ['get', 'rainScale'], 'rain_24h'],
        [
          'step',
          ['number', ['get', 'rain_24h']],
          'rain-0',
          2,
          'rain-1',
          5,
          'rain-2',
          10,
          'rain-3',
          50,
          'rain-4',
          100,
          'rain-5',
          Infinity,
          'rain-6',
        ],
        ['==', ['get', 'rainScale'], 'rain_60min'],
        [
          'step',
          ['number', ['get', 'rain_60min']],
          'rain-0',
          1,
          'rain-1',
          2.5,
          'rain-2',
          5,
          'rain-3',
          10,
          'rain-4',
          50,
          'rain-5',
          Infinity,
          'rain-6',
        ],
        [
          'step',
          ['number', ['get', 'rain_live']],
          'rain-0',
          0.2,
          'rain-1',
          0.4,
          'rain-2',
          0.8,
          'rain-3',
          2,
          'rain-4',
          4,
          'rain-5',
          Infinity,
          'rain-6',
        ],
      ],
      'icon-size': 0.5,
      'icon-offset': [0, -39],
      'icon-allow-overlap': true,
    },
    paint: {
      'text-color': 'white',
    },
  } as AddLayerObject;

  if (options?.sourceLayer) {
    layer['source-layer'] = id;
  }

  return layer;
}

function WindLayer(id: string, options?: { sourceLayer: boolean }): AddLayerObject {
  const layer = {
    id,
    source: id,
    type: 'symbol',
    filter: ZoomFilter,
    layout: {
      'icon-image': [
        'case',
        ['>=', ['number', ['get', ['concat', ['get', 'windType'], '_angle']]], 0],
        [
          'step',
          ['number', ['get', ['concat', ['get', 'windType'], '_strength']]],
          'wind-0',
          11,
          'wind-1',
          19,
          'wind-2',
          28,
          'wind-3',
          38,
          'wind-4',
          49,
          'wind-5',
          61,
          'wind-6',
          74,
          'wind-7',
          88,
          'wind-8',
          102,
          'wind-9',
          117,
          'wind-10',
          Infinity,
          'wind-11',
        ],
        [
          'step',
          ['number', ['get', ['concat', ['get', 'windType'], '_strength']]],
          'wind-no0',
          11,
          'wind-no1',
          19,
          'wind-no2',
          28,
          'wind-no3',
          38,
          'wind-no4',
          49,
          'wind-no5',
          61,
          'wind-no6',
          74,
          'wind-no7',
          88,
          'wind-no8',
          102,
          'wind-no9',
          117,
          'wind-no10',
          Infinity,
          'wind-no11',
        ],
      ],
      'icon-size': 0.5,
      'icon-rotate': ['+', 180, ['number', ['get', ['concat', ['get', 'windType'], '_angle']]]],
      'icon-anchor': 'center',
      'icon-allow-overlap': true,
    },
    paint: {
      'text-color': 'white',
    },
  } as AddLayerObject;

  if (options?.sourceLayer) {
    layer['source-layer'] = id;
  }

  return layer;
}

export const Layers: Record<PinType, AddLayerObject> = {
  [PinType.TEMPERATURE]: TemperatureLayer('stations', { sourceLayer: true }),
  [PinType.RAIN]: RainLayer('stations', { sourceLayer: true }),
  [PinType.WIND]: WindLayer('stations', { sourceLayer: true }),
};

export const HomeLayers: Record<PinType, AddLayerObject> = {
  [PinType.TEMPERATURE]: TemperatureLayer('home-stations'),
  [PinType.RAIN]: RainLayer('home-stations'),
  [PinType.WIND]: WindLayer('home-stations'),
};

export const FriendLayers: Record<PinType, AddLayerObject> = {
  [PinType.TEMPERATURE]: TemperatureLayer('friend-stations'),
  [PinType.RAIN]: RainLayer('friend-stations'),
  [PinType.WIND]: WindLayer('friend-stations'),
};

export const FavoriteLayers: Record<PinType, AddLayerObject> = {
  [PinType.TEMPERATURE]: TemperatureLayer('favorite-stations'),
  [PinType.RAIN]: RainLayer('favorite-stations'),
  [PinType.WIND]: WindLayer('favorite-stations'),
};

export const SelectedLayer: AddLayerObject = {
  id: 'selected-station',
  type: 'symbol',
  source: 'selected-station',
  layout: {
    'icon-image': 'selected-station',
    'icon-size': 1 / 3,
    'icon-allow-overlap': true,
  },
};
